import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Plot from 'react-plotly.js';
import { cadenceService } from '../../services/cadenceService';
import { mergeLayout, PLOTLY_CONFIG } from '../../components/charts/chartTheme';
import { useSortableRows, SortableTh } from '../../components/tables/SortableTable';

// Cadence — earnings workbench inside ResearchOS. Two views:
//   Surprises  — 1) coverage monitor, 2) company beat/miss history, 3) leaderboard.
//   Trajectory — reported actuals continuing into the FORWARD consensus curve (latest
//                EstimateSnapshot per future quarter, with the analyst low/high band), plus each
//                quarter's revision drift since daily capture began (2026-09-17 — accrues daily,
//                can't be backfilled: FMP only serves the current consensus).
// Read-only; the daily 8 AM ET top-up keeps it fresh. Everything earnings lives here for now.

const isDark = () => typeof document !== 'undefined' && document.documentElement.classList.contains('dark');
const GOLD = '#c9a96e';

// ── formatting helpers (house rule: signed numbers are green/red) ──────────────────────────────
const pctColor = (v) =>
  v == null ? 'text-gray-400 dark:text-gray-500'
    : v > 0 ? 'text-green-600 dark:text-green-400'
      : v < 0 ? 'text-red-600 dark:text-red-400'
        : 'text-gray-500 dark:text-gray-400';
const fmtPct = (v, dp = 1) => (v == null ? '—' : `${v > 0 ? '+' : ''}${v.toFixed(dp)}%`);
const fmtNum = (v, dp = 2) => (v == null ? '—' : v.toFixed(dp));
const CHECK_BADGE = {
  ok: 'bg-green-500/10 text-green-600 dark:text-green-400',
  divergent: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
  missing: 'bg-gray-500/10 text-gray-500 dark:text-gray-400',
  unchecked: 'bg-gray-500/10 text-gray-400 dark:text-gray-500',
};

function Tile({ label, value, sub, valueClass = '' }) {
  return (
    <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-800">
      <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</div>
      <div className={`mt-1 font-serif text-2xl tabular-nums text-gray-900 dark:text-gray-100 ${valueClass}`}>{value}</div>
      {sub != null && <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{sub}</div>}
    </div>
  );
}

function Section({ title, right, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-800">
      <button onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between px-4 py-3 text-left">
        <span className="font-serif text-lg text-gray-900 dark:text-gray-100">{title}</span>
        <span className="flex items-center gap-3">{right}
          <span className="text-gray-400">{open ? '▾' : '▸'}</span></span>
      </button>
      {open && <div className="border-t border-gray-100 px-4 py-4 dark:border-gray-800/70">{children}</div>}
    </div>
  );
}

// stacked ok/divergent/missing health bar
function HealthBar({ v }) {
  const total = v?.total || 0;
  if (!total) return null;
  const seg = (n, cls) => (n ? <div className={cls} style={{ width: `${(100 * n) / total}%` }} /> : null);
  return (
    <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
      {seg(v.ok, 'bg-green-500')}{seg(v.divergent, 'bg-amber-500')}{seg(v.missing, 'bg-gray-400 dark:bg-gray-600')}
    </div>
  );
}

function VerificationLegend({ v }) {
  const total = v?.total || 0;
  const pct = (n) => (total ? `${((100 * n) / total).toFixed(1)}%` : '—');
  return (
    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600 dark:text-gray-300">
      <span><span className="mr-1 inline-block h-2 w-2 rounded-full bg-green-500" />ok {v.ok} ({pct(v.ok)})</span>
      <span><span className="mr-1 inline-block h-2 w-2 rounded-full bg-amber-500" />divergent {v.divergent} ({pct(v.divergent)})</span>
      <span><span className="mr-1 inline-block h-2 w-2 rounded-full bg-gray-400" />missing {v.missing} ({pct(v.missing)})</span>
    </div>
  );
}

// ── surprise rows table (shared by company history + leaderboard) ──────────────────────────────
function SurpriseTable({ rows, showCompany }) {
  if (!rows?.length) return <div className="py-6 text-center text-sm text-gray-500">No rows.</div>;
  return (
    <div className="overflow-x-auto scrollbar-thin">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 text-left text-[11px] uppercase tracking-wide text-gray-500 dark:border-gray-800 dark:text-gray-400">
            {showCompany && <th className="py-2 pr-3">Company</th>}
            <th className="py-2 pr-3">Reported</th>
            <th className="py-2 pr-3">Fiscal Q</th>
            <th className="py-2 pr-3">When</th>
            <th className="py-2 pr-3 text-right">EPS est</th>
            <th className="py-2 pr-3 text-right">EPS act</th>
            <th className="py-2 pr-3 text-right">EPS surp</th>
            <th className="py-2 pr-3 text-right">Rev surp</th>
            <th className="py-2 pr-3">Check</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-b border-gray-100 dark:border-gray-800/60">
              {showCompany && <td className="py-1.5 pr-3 text-gray-800 dark:text-gray-200">{r.company}</td>}
              <td className="py-1.5 pr-3 tabular-nums text-gray-700 dark:text-gray-300">{r.report_date}</td>
              <td className="py-1.5 pr-3 tabular-nums text-gray-500 dark:text-gray-400">{r.fiscal_date}</td>
              <td className="py-1.5 pr-3 uppercase text-gray-500 dark:text-gray-400">{r.report_time || '—'}</td>
              <td className="py-1.5 pr-3 text-right tabular-nums text-gray-700 dark:text-gray-300">{fmtNum(r.eps_estimate)}</td>
              <td className="py-1.5 pr-3 text-right tabular-nums text-gray-900 dark:text-gray-100">{fmtNum(r.eps_actual)}</td>
              <td className={`py-1.5 pr-3 text-right tabular-nums font-medium ${pctColor(r.eps_surprise_pct)}`}>{fmtPct(r.eps_surprise_pct)}</td>
              <td className={`py-1.5 pr-3 text-right tabular-nums ${pctColor(r.revenue_surprise_pct)}`}>{fmtPct(r.revenue_surprise_pct)}</td>
              <td className="py-1.5 pr-3">
                <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${CHECK_BADGE[r.estimate_check] || CHECK_BADGE.unchecked}`}>{r.estimate_check}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── TRAJECTORY VIEW ────────────────────────────────────────────────────────────────────────────
// Reported actuals (solid) → forward consensus (dashed, with the analyst low/high band), plus a
// sortable forward-quarter table whose rows expand into the revision history since capture began.
const fmtB = (v) => (v == null ? '—' : `$${(v / 1e9).toFixed(2)}B`);

// Per-period revision drill: the consensus FOR one selected fiscal period charted over observation
// time (x = the day each value was captured). This is the axis that deepens daily — capture began
// 2026-09-17 and cannot be backfilled, so today it spans days; in a year it spans a year.
function RevisionDrill({ q, K }) {
  const theme = isDark() ? 'dark' : 'light';
  if (!q.revisions?.length) return null;
  const pts = q.revisions.filter((r) => r[K.avg] != null);
  return (
    <div className="ml-4 border-l-2 border-brand-gold/40 py-1 pl-3">
      {pts.length >= 2 ? (
        <>
          <div className="pb-1 text-[10px] uppercase tracking-wide text-gray-400">
            Consensus for {q.fiscal_date} over time · {pts.length} observations
          </div>
          <Plot
            data={[{
              x: pts.map((r) => r.as_of_date), y: pts.map((r) => r[K.avg] / K.scale),
              type: 'scatter', mode: 'lines+markers',
              line: { color: GOLD, width: 1.6, shape: 'hv' }, marker: { size: 5, color: GOLD },
              hovertemplate: '%{x} · %{y:.3f}<extra></extra>',
            }]}
            layout={mergeLayout({ height: 160, margin: { l: 46, r: 10, t: 4, b: 26 }, showlegend: false }, theme)}
            config={PLOTLY_CONFIG} style={{ width: '100%' }} useResizeHandler />
        </>
      ) : (
        <div className="py-1 text-[11px] text-gray-400">
          One observation so far — the revision chart appears as daily capture accrues more points.
        </div>
      )}
      {q.revisions.map((r) => (
        <div key={r.as_of_date} className="flex gap-4 py-0.5 text-xs tabular-nums text-gray-600 dark:text-gray-300">
          <span className="w-24 text-gray-400 dark:text-gray-500">{r.as_of_date}</span>
          <span>EPS {fmtNum(r.eps_avg)}</span>
          <span>Rev {fmtB(r.revenue_avg)}</span>
          <span className="text-gray-400 dark:text-gray-500">{r.n_analysts_eps ?? '—'} analysts</span>
        </div>
      ))}
    </div>
  );
}

// Sortable forward-consensus table (house SortableTable primitive) — one instance for the
// quarterly curve, one for the fiscal-year (annual) curve. Row click opens the revision drill.
function ForwardTable({ rows, K, title, note, labelCol }) {
  const cols = useMemo(() => [
    { key: 'fiscal_date', type: 'string', get: (r) => r.fiscal_date },
    { key: 'consensus', type: 'number', get: (r) => r[K.avg] },
    { key: 'low', type: 'number', get: (r) => r[K.lo] },
    { key: 'high', type: 'number', get: (r) => r[K.hi] },
    { key: 'analysts', type: 'number', get: (r) => r.n_analysts_eps },
    { key: 'drift', type: 'abs', get: (r) => r.eps_drift_pct },
    { key: 'revs', type: 'number', get: (r) => r.n_revisions },
  ], [K.avg, K.lo, K.hi]);
  const { sorted, toggle, indicator } = useSortableRows(rows || [], cols);
  const [openFy, setOpenFy] = useState(null);

  return (
    <div className="mt-4">
      <div className="mb-1 text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">{title}</div>
      {sorted.length === 0 ? (
        <div className="py-2 text-xs text-gray-400">{note || 'No data yet.'}</div>
      ) : (
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-[11px] uppercase tracking-wide text-gray-500 dark:border-gray-800 dark:text-gray-400">
                <SortableTh label={labelCol} colKey="fiscal_date" toggle={toggle} indicator={indicator} align="left" className="py-2 pr-3 text-left" />
                <SortableTh label="Consensus" colKey="consensus" toggle={toggle} indicator={indicator} className="py-2 pr-3" />
                <SortableTh label="Low" colKey="low" toggle={toggle} indicator={indicator} className="py-2 pr-3" />
                <SortableTh label="High" colKey="high" toggle={toggle} indicator={indicator} className="py-2 pr-3" />
                <SortableTh label="Analysts" colKey="analysts" toggle={toggle} indicator={indicator} className="py-2 pr-3" />
                <SortableTh label="Δ since capture" colKey="drift" toggle={toggle} indicator={indicator} className="py-2 pr-3" />
                <SortableTh label="Revisions" colKey="revs" toggle={toggle} indicator={indicator} className="py-2 pr-3" />
              </tr>
            </thead>
            <tbody>
              {sorted.map((f) => (
                <React.Fragment key={f.fiscal_date}>
                  <tr onClick={() => setOpenFy(openFy === f.fiscal_date ? null : f.fiscal_date)}
                    className="cursor-pointer border-b border-gray-100 hover:bg-gray-50 dark:border-gray-800/60 dark:hover:bg-gray-800/40">
                    <td className="py-1.5 pr-3 tabular-nums text-gray-700 dark:text-gray-300">{f.fiscal_date}</td>
                    <td className="py-1.5 pr-3 text-right tabular-nums text-gray-900 dark:text-gray-100">{K.fmt(f[K.avg])}</td>
                    <td className="py-1.5 pr-3 text-right tabular-nums text-gray-500 dark:text-gray-400">{K.fmt(f[K.lo])}</td>
                    <td className="py-1.5 pr-3 text-right tabular-nums text-gray-500 dark:text-gray-400">{K.fmt(f[K.hi])}</td>
                    <td className="py-1.5 pr-3 text-right tabular-nums text-gray-500 dark:text-gray-400">{f.n_analysts_eps ?? '—'}</td>
                    <td className={`py-1.5 pr-3 text-right tabular-nums font-medium ${pctColor(f.eps_drift_pct)}`}>{fmtPct(f.eps_drift_pct, 2)}</td>
                    <td className="py-1.5 pr-3 text-right tabular-nums text-gray-500 dark:text-gray-400">{f.n_revisions}{f.n_revisions > 0 && <span className="ml-1 text-gray-400">{openFy === f.fiscal_date ? '▾' : '▸'}</span>}</td>
                  </tr>
                  {openFy === f.fiscal_date && (
                    <tr className="border-b border-gray-100 dark:border-gray-800/60">
                      <td colSpan={7}><RevisionDrill q={f} K={K} /></td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// THE headline of the Trajectory tab: one selected future period (a quarter or a fiscal year),
// its consensus charted over OBSERVATION time — the raw material of EPS revision momentum.
// Reads the revisions arrays already in the trajectory payload; no extra API call.
function RevisionMomentum({ data, K }) {
  const theme = isDark() ? 'dark' : 'light';
  const periods = useMemo(() => [
    ...(data.forward || []).map((f) => ({ id: `Q:${f.fiscal_date}`, label: `Q ${f.fiscal_date}`, row: f })),
    ...(data.forward_annual || []).map((f) => ({ id: `FY:${f.fiscal_date}`, label: `FY ${String(f.fiscal_date).slice(0, 4)}`, row: f })),
  ], [data]);
  const [sel, setSel] = useState(null);
  useEffect(() => { setSel(null); }, [data.ticker]);
  const cur = periods.find((p) => p.id === sel) || periods[0];
  if (!cur) return null;
  const pts = (cur.row.revisions || []).filter((r) => r[K.avg] != null);
  const first = pts[0]; const lastP = pts[pts.length - 1];
  const drift = (pts.length >= 2 && first[K.avg]) ? ((lastP[K.avg] / first[K.avg]) - 1) * 100 : null;
  return (
    <div className="mb-5 rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-[#161b22]">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
          Revision momentum · consensus for one period, through time
        </div>
        <div className="flex flex-wrap items-center gap-1">
          {periods.map((p) => (
            <button key={p.id} onClick={() => setSel(p.id)}
              className={`rounded px-2 py-0.5 text-[11px] tabular-nums transition-colors ${(cur.id === p.id)
                ? 'bg-brand-gold text-white'
                : 'border border-gray-200 text-gray-500 hover:text-gray-800 dark:border-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}>
              {p.label}
            </button>
          ))}
        </div>
      </div>
      <div className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Tile label="Consensus now" value={K.fmt(cur.row[K.avg])} sub={cur.row.as_of ? `as of ${cur.row.as_of}` : ''} />
        <Tile label="Δ since capture" value={fmtPct(drift, 2)} valueClass={pctColor(drift)} sub={first ? `from ${first.as_of_date}` : ''} />
        <Tile label="Analysts" value={cur.row.n_analysts_eps ?? '—'} sub="latest snapshot" />
        <Tile label="Observations" value={pts.length} sub="deepens daily" />
      </div>
      {pts.length >= 2 ? (
        <Plot
          data={[{
            x: pts.map((r) => r.as_of_date), y: pts.map((r) => r[K.avg] / K.scale),
            type: 'scatter', mode: 'lines+markers',
            line: { color: GOLD, width: 1.8, shape: 'hv' }, marker: { size: 6, color: GOLD },
            hovertemplate: '%{x} · %{y:.3f}<extra></extra>',
          }]}
          layout={mergeLayout({
            height: 300, margin: { l: 56, r: 14, t: 8, b: 34 }, showlegend: false,
            yaxis: { title: { text: K.axis, font: { size: 11 } } },
          }, theme)}
          config={PLOTLY_CONFIG} style={{ width: '100%' }} useResizeHandler />
      ) : (
        <div className="py-5 text-center text-sm text-gray-400">
          {pts.length === 1 ? 'One observation so far — the revision path draws itself as daily capture accrues.' : 'No snapshots for this period yet.'}
        </div>
      )}
    </div>
  );
}

function TrajectoryView() {
  const [q, setQ] = useState('');
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);
  const [metric, setMetric] = useState('eps');      // 'eps' | 'revenue'
  const [view, setView] = useState('revisions');    // 'revisions' (one period through time) | 'curve' (actuals + all forward periods)
  const theme = isDark() ? 'dark' : 'light';

  const search = (e) => {
    e?.preventDefault();
    const t = q.trim().toUpperCase();
    if (!t) return;
    setLoading(true); setErr(''); setData(null);
    cadenceService.getTrajectory(t)
      .then((r) => setData(r.data))
      .catch((error) => setErr(error?.response?.status === 404 ? `“${t}” isn’t on the security spine.` : 'Lookup failed.'))
      .finally(() => setLoading(false));
  };

  const K = metric === 'eps'
    ? { act: 'eps_actual', est: 'eps_estimate', avg: 'eps_avg', lo: 'eps_low', hi: 'eps_high', scale: 1, fmt: fmtNum, axis: 'EPS ($)' }
    : { act: 'revenue_actual', est: 'revenue_estimate', avg: 'revenue_avg', lo: 'revenue_low', hi: 'revenue_high', scale: 1e9, fmt: fmtB, axis: 'Revenue ($B)' };

  const traces = useMemo(() => {
    if (!data) return [];
    const rep = (data.reported || []).filter((r) => r[K.act] != null);
    const fwd = (data.forward || []).filter((f) => f[K.avg] != null);
    const t = [];
    // analyst low/high band on the forward quarters
    const band = fwd.filter((f) => f[K.lo] != null && f[K.hi] != null);
    if (band.length > 1) {
      t.push({
        x: [...band.map((f) => f.fiscal_date), ...band.map((f) => f.fiscal_date).reverse()],
        y: [...band.map((f) => f[K.hi] / K.scale), ...band.map((f) => f[K.lo] / K.scale).reverse()],
        fill: 'toself', fillcolor: 'rgba(201,169,110,0.15)', line: { width: 0 },
        hoverinfo: 'skip', showlegend: false, type: 'scatter',
      });
    }
    // what was expected at each past print — open gray markers
    const est = rep.filter((r) => r[K.est] != null);
    if (est.length) {
      t.push({
        x: est.map((r) => r.fiscal_date), y: est.map((r) => r[K.est] / K.scale),
        mode: 'markers', name: 'Expected at print', type: 'scatter',
        marker: { symbol: 'circle-open', size: 7, color: '#9ca3af' },
      });
    }
    // delivered actuals — solid
    if (rep.length) {
      t.push({
        x: rep.map((r) => r.fiscal_date), y: rep.map((r) => r[K.act] / K.scale),
        mode: 'lines+markers', name: 'Actual', type: 'scatter',
        line: { color: theme === 'dark' ? '#e5e7eb' : '#1f2937', width: 2 }, marker: { size: 6 },
      });
    }
    // forward consensus — dashed gold, anchored on the last actual for continuity
    if (fwd.length) {
      const anchor = rep.length ? [{ fiscal_date: rep[rep.length - 1].fiscal_date, v: rep[rep.length - 1][K.act] }] : [];
      t.push({
        x: [...anchor.map((a) => a.fiscal_date), ...fwd.map((f) => f.fiscal_date)],
        y: [...anchor.map((a) => a.v / K.scale), ...fwd.map((f) => f[K.avg] / K.scale)],
        mode: 'lines+markers', name: 'Street consensus', type: 'scatter',
        line: { color: GOLD, width: 2, dash: 'dash' }, marker: { size: 6, color: GOLD },
      });
    }
    return t;
  }, [data, K.act, K.est, K.avg, K.lo, K.hi, K.scale, theme]);

  // The sortable table logic lives in the shared <ForwardTable> component (rendered twice below —
  // quarterly + annual curves), which wraps the house useSortableRows/SortableTh primitive.
  return (
    <div className="space-y-5">
      <Section title="Earnings trajectory">
        <form onSubmit={search} className="mb-3 flex flex-wrap items-center gap-2">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ticker (e.g. AAPL, NVDA, JPM)"
            className="w-full max-w-xs rounded-md border border-gray-300 bg-transparent px-3 py-1.5 text-sm text-gray-900 placeholder-gray-400 focus:border-brand-gold focus:outline-none dark:border-gray-700 dark:text-gray-100" />
          <button type="submit" className="rounded-md bg-brand-gold px-3 py-1.5 text-sm font-medium text-white hover:opacity-90">Chart</button>
          <div className="ml-auto flex overflow-hidden rounded-md border border-gray-200 text-xs dark:border-gray-700">
            {['eps', 'revenue'].map((m) => (
              <button key={m} onClick={(e) => { e.preventDefault(); setMetric(m); }}
                className={`px-2.5 py-1 uppercase tracking-wide ${metric === m
                  ? 'bg-brand-gold text-white'
                  : 'text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'}`}>
                {m === 'eps' ? 'EPS' : 'Revenue'}
              </button>
            ))}
          </div>
        </form>
        {loading && <div className="text-sm text-gray-500">Loading…</div>}
        {err && <div className="text-sm text-red-500">{err}</div>}
        {data && (
          <>
            <div className="mb-2 flex flex-wrap items-baseline gap-x-4 gap-y-1">
              <span className="font-serif text-lg text-gray-900 dark:text-gray-100">{data.company}</span>
              <span className="text-sm text-gray-500 dark:text-gray-400">{data.ticker}</span>
              <span className="text-xs text-gray-400 dark:text-gray-500">
                {data.reported?.length || 0} reported · {data.forward?.length || 0} forward quarters
              </span>
            </div>
            <div className="mb-3 flex overflow-hidden rounded-md border border-gray-200 text-xs dark:border-gray-700" style={{ width: 'fit-content' }}>
              {[['revisions', 'Revision path'], ['curve', 'Forward curve']].map(([v, lbl]) => (
                <button key={v} onClick={() => setView(v)}
                  className={`px-2.5 py-1 uppercase tracking-wide ${view === v
                    ? 'bg-brand-gold text-white'
                    : 'text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'}`}>
                  {lbl}
                </button>
              ))}
            </div>

            {view === 'revisions' ? (
              <RevisionMomentum data={data} K={K} />
            ) : (
              traces.length > 0 ? (
                <Plot data={traces}
                  layout={mergeLayout({
                    height: 380, margin: { l: 56, r: 14, t: 8, b: 40 },
                    showlegend: true, legend: { orientation: 'h', y: -0.18 },
                    yaxis: { title: { text: K.axis, font: { size: 11 } } },
                  }, theme)}
                  config={PLOTLY_CONFIG} style={{ width: '100%' }} useResizeHandler />
              ) : <div className="py-6 text-center text-sm text-gray-500">No {metric} data for this name.</div>
            )}

            <ForwardTable rows={data.forward} K={K} labelCol="Fiscal Q" title="Forward quarters"
              note="No forward-quarter consensus stored for this name yet." />
            <ForwardTable rows={data.forward_annual} K={K} labelCol="Fiscal year" title="Fiscal years · annual consensus"
              note="Annual (FY) capture began 2026-09-24 — the fiscal-year curve and its revision history appear after the first daily annual snapshot." />
            <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">
              Forward consensus = latest daily snapshot per future quarter; band = analyst low/high.
              “Δ since capture” is the consensus move since daily capture began{data.capture?.from ? ` (${data.capture.from})` : ''} —
              revision history accrues from that date and deepens daily; it cannot be backfilled.
            </p>
          </>
        )}
        {!data && !loading && !err && (
          <div className="py-6 text-center text-sm text-gray-500">Search a ticker to chart its earnings trajectory.</div>
        )}
      </Section>
    </div>
  );
}

export default function CadencePage() {
  const [view, setView] = useState('surprises');   // 'surprises' | 'trajectory'
  const [summary, setSummary] = useState(null);
  const [sumErr, setSumErr] = useState('');
  const [leaders, setLeaders] = useState(null);
  const [company, setCompany] = useState(null);
  const [companyErr, setCompanyErr] = useState('');
  const [q, setQ] = useState('');
  const [loadingCo, setLoadingCo] = useState(false);

  const loadSummary = useCallback(() => {
    cadenceService.getSummary()
      .then((r) => { setSummary(r.data); setSumErr(''); })
      .catch((e) => setSumErr(e?.response?.status === 403 ? 'You need trading access to view Cadence.' : 'Failed to load coverage.'));
  }, []);

  useEffect(() => {
    loadSummary();
    cadenceService.getLeaderboard({ days: 120, limit: 20 }).then((r) => setLeaders(r.data)).catch(() => {});
  }, [loadSummary]);

  const search = (e) => {
    e?.preventDefault();
    const t = q.trim().toUpperCase();
    if (!t) return;
    setLoadingCo(true); setCompanyErr(''); setCompany(null);
    cadenceService.getCompany(t)
      .then((r) => setCompany(r.data))
      .catch((err) => setCompanyErr(err?.response?.status === 404 ? `“${t}” isn’t in the S&P 500 coverage yet.` : 'Lookup failed.'))
      .finally(() => setLoadingCo(false));
  };

  const V = summary?.verification;
  const M = summary?.modern;

  return (
    <div className="space-y-5">
      <div>
        <h2 className="font-serif text-2xl text-gray-900 dark:text-gray-100">Cadence</h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Quarterly earnings — what analysts <span className="italic">expected</span> vs what companies <span className="italic">delivered</span>, on EPS and revenue.
          One row per company-quarter, verified against a second source, refreshed daily.
        </p>
        <div className="mt-3 flex overflow-hidden rounded-md border border-gray-200 text-xs dark:border-gray-700" style={{ width: 'fit-content' }}>
          {[['surprises', 'Surprises'], ['trajectory', 'Trajectory']].map(([v, label]) => (
            <button key={v} onClick={() => setView(v)}
              className={`px-3 py-1.5 uppercase tracking-wide ${view === v
                ? 'bg-brand-gold text-white'
                : 'text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {view === 'trajectory' && <TrajectoryView />}

      {view === 'surprises' && (<div className="space-y-5">
      {/* 1) COVERAGE MONITOR — this wrapper div closes just before the page's final </div>, tagged
          with the matching `end surprises wrapper` comment */}
      <Section title="Coverage" right={
        <button onClick={(e) => { e.stopPropagation(); loadSummary(); }}
          className="rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-600 hover:border-brand-gold dark:border-gray-700 dark:text-gray-300">↻ refresh</button>}>
        {sumErr && <div className="text-sm text-red-500">{sumErr}</div>}
        {!sumErr && !summary && <div className="text-sm text-gray-500">Loading…</div>}
        {summary && (
          <>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Tile label="Quarters stored" value={(summary.total_rows || 0).toLocaleString()} />
              <Tile label="Companies" value={summary.securities || 0} sub="S&P 500 members" />
              <Tile label="History" value={summary.date_range?.from?.slice(0, 4) || '—'}
                sub={`through ${summary.date_range?.to || '—'}`} />
              <Tile label="Verified (2020+)"
                value={M?.total ? `${((100 * M.ok) / M.total).toFixed(0)}%` : '—'}
                valueClass="text-green-600 dark:text-green-400" sub="modern-era ok rate" />
            </div>
            {V && (
              <div className="mt-4">
                <div className="mb-1 text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Estimate verification (all history)</div>
                <HealthBar v={V} />
                <VerificationLegend v={V} />
                <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">
                  Every quarter is stored regardless of flag. “divergent” = our two estimate sources disagree &gt;5% (mostly deep-history split-adjusted pennies); “missing” = no second source to check. 2020+ is {M?.total ? `${((100 * M.ok) / M.total).toFixed(1)}%` : '—'} clean.
                </p>
                {summary.last_updated && (
                  <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">Last updated {new Date(summary.last_updated).toLocaleString()}.</p>
                )}
              </div>
            )}
            {(summary.top_beats?.length > 0 || summary.top_misses?.length > 0) && (
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <div className="mb-1 text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Biggest recent beats</div>
                  {(summary.top_beats || []).slice(0, 5).map((b) => (
                    <div key={b.id} className="flex justify-between py-0.5 text-sm">
                      <span className="text-gray-700 dark:text-gray-300">{b.company}</span>
                      <span className={`tabular-nums ${pctColor(b.eps_surprise_pct)}`}>{fmtPct(b.eps_surprise_pct)}</span>
                    </div>
                  ))}
                </div>
                <div>
                  <div className="mb-1 text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Biggest recent misses</div>
                  {(summary.top_misses || []).slice(0, 5).map((b) => (
                    <div key={b.id} className="flex justify-between py-0.5 text-sm">
                      <span className="text-gray-700 dark:text-gray-300">{b.company}</span>
                      <span className={`tabular-nums ${pctColor(b.eps_surprise_pct)}`}>{fmtPct(b.eps_surprise_pct)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </Section>

      {/* 2) COMPANY LOOKUP */}
      <Section title="Company history">
        <form onSubmit={search} className="mb-3 flex gap-2">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ticker (e.g. AAPL, NVDA, JPM)"
            className="w-full max-w-xs rounded-md border border-gray-300 bg-transparent px-3 py-1.5 text-sm text-gray-900 placeholder-gray-400 focus:border-brand-gold focus:outline-none dark:border-gray-700 dark:text-gray-100" />
          <button type="submit" className="rounded-md bg-brand-gold px-3 py-1.5 text-sm font-medium text-white hover:opacity-90">Look up</button>
        </form>
        {loadingCo && <div className="text-sm text-gray-500">Loading…</div>}
        {companyErr && <div className="text-sm text-red-500">{companyErr}</div>}
        {company && (
          <>
            <div className="mb-3 flex flex-wrap items-baseline gap-x-4 gap-y-1">
              <span className="font-serif text-lg text-gray-900 dark:text-gray-100">{company.company}</span>
              <span className="text-sm text-gray-500 dark:text-gray-400">{company.ticker}</span>
              {company.stats?.quarters != null && <span className="text-xs text-gray-500 dark:text-gray-400">{company.stats.quarters} quarters</span>}
              {company.stats?.beat_rate != null && (
                <span className="text-xs">beat rate <span className={pctColor(company.stats.beat_rate - 50)}>{company.stats.beat_rate}%</span></span>
              )}
              {company.stats?.avg_surprise_pct != null && (
                <span className="text-xs">avg surprise <span className={pctColor(company.stats.avg_surprise_pct)}>{fmtPct(company.stats.avg_surprise_pct)}</span></span>
              )}
            </div>
            <SurpriseTable rows={company.rows} />
          </>
        )}
      </Section>

      {/* 3) LEADERBOARD */}
      <Section title="Recent movers" right={leaders?.window_days && (
        <span className="text-xs text-gray-400">last {leaders.window_days}d</span>)}>
        {!leaders && <div className="text-sm text-gray-500">Loading…</div>}
        {leaders && <SurpriseTable rows={leaders.rows} showCompany />}
        {leaders?.rows?.length > 0 && (
          <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">
            Ranked by |EPS surprise| on estimates ≥ $0.10. Very large %s are usually GAAP one-time items (e.g. mega-cap investment gains) — compare the est vs act columns.
          </p>
        )}
      </Section>
      </div>)}{/* end surprises wrapper — pairs with the `view === 'surprises' && (<div` opening above */}
    </div>
  );
}
